// Physics → Section 9: Forces and Motion (KS3, sourced from the
// Grade9Ready science PDF). 10 topics as specified by the user.
// Same rich-content template as the other section files.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'physics/forces-and-motion';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 9.1  SPEED
// =====================================================================
const SPEED_TOPIC = {
  id: 'phys-9.1',
  title: 'Speed',
  slug: 'speed',
  pageRef: 'Section 9 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What speed measures',
        body:
          'Speed tells you how fast something is moving — the distance covered per unit of time. The standard formula is speed = distance ÷ time. SI units: distance in metres (m), time in seconds (s), so speed in metres per second (m/s). For longer journeys we often use km/h or miles per hour, but the maths is the same.',
        check: {
          question: 'A car travels 80 m in 4 s. Its speed?',
          options: ['10 m/s', '20 m/s', '320 m/s', '0.05 m/s'],
          answer: '20 m/s',
          explainer: '80 ÷ 4 = 20 m/s.',
        },
      },
      {
        heading: 'Average vs instantaneous',
        body:
          'Average speed = total distance ÷ total time. It hides the changes along the way — a car might go 50 mph for half the trip and 80 mph for the rest, but its AVERAGE could be 65 mph. Instantaneous speed is the speed AT a specific moment — what the speedometer shows. Always check which is being asked.',
        check: {
          question: 'A car travels 100 km in 2 hours. Average speed?',
          options: ['25 km/h', '50 km/h', '100 km/h', '200 km/h'],
          answer: '50 km/h',
          explainer: '100 ÷ 2 = 50 km/h.',
        },
      },
      {
        heading: 'Rearranging the formula',
        body:
          "From speed = distance ÷ time, you can get the other two: distance = speed × time, and time = distance ÷ speed. Useful triangle: D on top, S × T below. Cover the variable you want and read the rest off. Example: a runner moves at 6 m/s for 30 s → distance = 6 × 30 = 180 m.",
        check: {
          question: 'A bus drives at 15 m/s for 1 minute. Distance covered?',
          options: ['15 m', '60 m', '900 m', '1500 m'],
          answer: '900 m',
          explainer: '1 minute = 60 s. 15 × 60 = 900 m.',
        },
      },
    ],
    keyPoints: [
      'Speed = distance ÷ time',
      'SI units: m/s (other common: km/h, mph)',
      'Average ≠ instantaneous (which a speedometer shows)',
      'Distance = speed × time',
      'Time = distance ÷ speed',
      'Always convert minutes/hours to seconds for m/s answers',
    ],
    workedExamples: [
      {
        problem: 'A train covers 540 km in 3 hours. (a) What is its average speed in km/h? (b) Convert to m/s.',
        steps: [
          { step: 1, instruction: 'Average speed', working: '540 ÷ 3 = 180 km/h' },
          { step: 2, instruction: 'Convert', working: '180 km/h × 1000 ÷ 3600 = 50 m/s' },
        ],
        answer: '180 km/h or 50 m/s.',
        commonMistake: 'Forgetting to convert km → m AND hours → s when changing units.',
      },
    ],
    whyItWorks:
      'Speed is the most basic motion idea. Once you have it, you can analyse pretty much any journey: just multiply by time to find distance, or divide a distance by a time to find a speed. The arithmetic is simple — the trick is keeping the units consistent.',
  },
  glossary: [
    { term: 'Speed', definition: 'How fast something is moving — distance per unit of time.' },
    { term: 'Average speed', definition: 'Total distance ÷ total time for a whole journey.' },
    { term: 'Instantaneous speed', definition: 'Speed at a specific instant — what a speedometer shows.' },
    { term: 'Metre per second (m/s)', definition: 'The SI unit of speed.' },
  ],
  examQuestion: {
    prompt:
      'A cyclist travels 1500 m in 2 minutes. (a) Calculate the average speed in m/s. (b) Predict the distance she would cover in 30 s at this speed. (c) State the difference between average and instantaneous speed. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Time = 120 s; speed = 1500 ÷ 120 = 12.5 m/s',
      'Distance = 12.5 × 30 = 375 m',
      'Average speed = total distance ÷ total time over a whole journey',
      'Instantaneous speed = speed at a specific moment',
      '(Average can hide changes; instantaneous is the speedometer reading)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.1.w1', type: 'text', question: 'Speed = distance ÷ ___ .', answer: 'time', hint1: '4 letters', hint2: 'How long it takes', hint3: 'Starts with t' },
      { id: 'phys-9.1.w2', type: 'number', question: '60 m in 5 s. Speed (m/s)?', answer: '12', hint1: '60 ÷ 5', hint2: 'Two digits', hint3: 'Twelve' },
      { id: 'phys-9.1.w3', type: 'choice', question: 'SI unit of speed?', options: ['m/s', 'km/h', 'mph', 's/m'], answer: 'm/s', hint1: 'Metres + seconds', hint2: 'Three characters', hint3: 'Used in physics' },
    ],
    level1: [
      { id: 'phys-9.1.1', type: 'number', question: 'Speed 8 m/s, time 30 s. Distance (m)?', answer: '240', hint1: 'Multiply', hint2: 'Three digits', hint3: '8 × 30' },
      { id: 'phys-9.1.2', type: 'number', question: 'Distance 100 m, speed 4 m/s. Time (s)?', answer: '25', hint1: 'Divide', hint2: 'Two digits', hint3: '100 ÷ 4' },
    ],
    level2: [
      { id: 'phys-9.1.3', type: 'number', question: 'Travelled 720 m in 1 minute. Speed (m/s)?', answer: '12', hint1: 'Convert min → s', hint2: '720 ÷ 60', hint3: 'Two digits' },
      { id: 'phys-9.1.4', type: 'choice', question: 'A speedometer measures…', options: ['Average speed', 'Instantaneous speed', 'Distance', 'Time'], answer: 'Instantaneous speed', hint1: 'Right now', hint2: 'At this moment', hint3: 'Not whole journey' },
    ],
    level3: [
      { id: 'phys-9.1.5', type: 'number', question: 'A car travels 36 km in 30 minutes. Speed in m/s?', answer: '20', hint1: '36 km = 36000 m, 30 min = 1800 s', hint2: '36000 ÷ 1800', hint3: 'Two digits' },
      { id: 'phys-9.1.6', type: 'text', question: 'D = S × T comes from rearranging the speed formula. Cover the variable you want and read the rest from the formula ___ .', answer: 'triangle', hint1: '8 letters', hint2: 'Three-corner shape', hint3: 'Starts with t' },
    ],
  },
};

// =====================================================================
// 9.2  DISTANCE-TIME GRAPHS
// =====================================================================
const DT_TOPIC = {
  id: 'phys-9.2',
  title: 'Distance-Time Graphs',
  slug: 'distance-time-graphs',
  pageRef: 'Section 9 · Topic 2',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'How to read one',
        body:
          'On a distance-time graph, time is on the x-axis and distance is on the y-axis. A horizontal line means stationary (distance not changing). A straight diagonal line means constant speed. The STEEPER the line, the FASTER the speed. A curve means changing speed (acceleration if it gets steeper, deceleration if it flattens out).',
        check: {
          question: 'A horizontal section on a distance-time graph means the object is…',
          options: ['Speeding up', 'Slowing down', 'Stationary', 'Going backwards'],
          answer: 'Stationary',
          explainer: 'No change in distance over time = no movement.',
        },
      },
      {
        heading: 'Speed = gradient',
        body:
          'On a distance-time graph, the speed is the GRADIENT (slope) of the line. Gradient = change in y ÷ change in x = distance ÷ time. So a line going from (0, 0) to (10 s, 50 m) has speed 50/10 = 5 m/s. The steeper the slope, the higher the speed.',
        check: {
          question: 'A line goes from (0 s, 0 m) to (4 s, 20 m). Speed?',
          options: ['1 m/s', '4 m/s', '5 m/s', '20 m/s'],
          answer: '5 m/s',
          explainer: '20 ÷ 4 = 5 m/s.',
        },
      },
      {
        heading: 'Common shapes',
        body:
          'Steady walk: straight line. Stop-start: horizontal then sloped sections. Accelerating car: a curve that gets STEEPER. Decelerating car: a curve that flattens. Returning home: line that comes BACK DOWN to zero distance. Practice sketching these — exam questions often give a graph and ask you to describe the journey.',
        check: {
          question: 'On a distance-time graph, a curve that gets STEEPER means the object is…',
          options: ['Stationary', 'Constant speed', 'Accelerating', 'Decelerating'],
          answer: 'Accelerating',
          explainer: 'Steeper means faster — speed is increasing.',
        },
      },
    ],
    keyPoints: [
      'Time on x; distance on y',
      'Horizontal line = stationary',
      'Straight slope = constant speed',
      'Steeper = faster',
      'Speed = gradient (distance/time)',
      'Curve steepening = accelerating; flattening = decelerating',
    ],
    workedExamples: [
      {
        problem: 'A graph rises straight from (0,0) to (10s, 30 m), is horizontal until 15 s, then rises again to (20 s, 60 m). Describe the journey and find the speed in each section.',
        steps: [
          { step: 1, instruction: '0–10 s', working: '0 → 30 m in 10 s → speed 3 m/s' },
          { step: 2, instruction: '10–15 s', working: 'Distance unchanged → stationary (speed 0)' },
          { step: 3, instruction: '15–20 s', working: '30 → 60 m in 5 s → speed 6 m/s' },
        ],
        answer: 'Walks at 3 m/s, stops for 5 s, then walks at 6 m/s.',
        commonMistake: 'Reading total distance from the y-axis but forgetting to subtract the start of each section.',
      },
    ],
    whyItWorks:
      'Distance-time graphs let you SEE a journey at a glance — every change of speed becomes a change of slope. They\u2019re especially powerful for comparing two journeys (e.g. car vs train) on the same axes.',
  },
  glossary: [
    { term: 'Distance-time graph', definition: 'A graph with time on the x-axis and distance on the y-axis.' },
    { term: 'Gradient', definition: 'The slope of a line on a graph = change in y ÷ change in x.' },
    { term: 'Stationary', definition: 'Not moving (horizontal line on a distance-time graph).' },
  ],
  examQuestion: {
    prompt:
      'A distance-time graph shows a straight line from (0, 0) to (8 s, 32 m), then horizontal until 12 s, then up to (20 s, 80 m). (a) Describe what is happening in each section. (b) Calculate the speed in each moving section. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      '0–8 s: moving at constant speed',
      '8–12 s: stationary',
      '12–20 s: moving at a faster constant speed',
      'Speed 1 = 32 ÷ 8 = 4 m/s',
      'Speed 2 = (80−32) ÷ (20−12) = 48 ÷ 8 = 6 m/s',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.2.w1', type: 'choice', question: 'Horizontal line on a D-T graph means…', options: ['Constant speed', 'Stationary', 'Accelerating', 'Going back'], answer: 'Stationary', hint1: 'No movement', hint2: 'Distance unchanged', hint3: 'Resting' },
      { id: 'phys-9.2.w2', type: 'choice', question: 'Steeper line means…', options: ['Slower', 'Faster', 'Stopped', 'No change'], answer: 'Faster', hint1: 'Bigger gradient', hint2: 'More distance per second', hint3: 'Quicker' },
      { id: 'phys-9.2.w3', type: 'number', question: 'Line (0,0) to (5 s, 25 m). Speed (m/s)?', answer: '5', hint1: '25 ÷ 5', hint2: 'Single digit', hint3: 'Five' },
    ],
    level1: [
      { id: 'phys-9.2.1', type: 'choice', question: 'Speed = ___ on a distance-time graph.', options: ['Area under line', 'Gradient of line', 'y-intercept', 'x-intercept'], answer: 'Gradient of line', hint1: 'Slope', hint2: 'Δd ÷ Δt', hint3: 'Rise over run' },
      { id: 'phys-9.2.2', type: 'text', question: 'A curve that gets steeper means the object is ___ .', answer: 'accelerating', hint1: '12 letters', hint2: 'Speeding up', hint3: 'Starts with a' },
    ],
    level2: [
      { id: 'phys-9.2.3', type: 'number', question: 'A line from (2 s, 0 m) to (10 s, 40 m). Speed?', answer: '5', hint1: '40 ÷ 8', hint2: 'Single digit', hint3: 'Five' },
      { id: 'phys-9.2.4', type: 'choice', question: 'Object returning to start would show line that…', options: ['Stays flat', 'Goes back down to zero', 'Goes off to the side', 'Disappears'], answer: 'Goes back down to zero', hint1: 'Distance decreasing', hint2: 'Same distance back', hint3: 'Returning home' },
    ],
    level3: [
      { id: 'phys-9.2.5', type: 'choice', question: 'A flattening curve on a distance-time graph means…', options: ['Speed increasing', 'Speed decreasing', 'Stationary', 'Going backwards'], answer: 'Speed decreasing', hint1: 'Less steep', hint2: 'Slowing down', hint3: 'Decelerating' },
      { id: 'phys-9.2.6', type: 'text', question: 'On a distance-time graph, time is on the ___-axis.', answer: 'x', hint1: 'Single letter', hint2: 'Horizontal axis', hint3: 'Letter' },
    ],
  },
};

// =====================================================================
// 9.3  RELATIVE MOTION
// =====================================================================
const RELATIVE_TOPIC = {
  id: 'phys-9.3',
  title: 'Relative Motion',
  slug: 'relative-motion',
  pageRef: 'Section 9 · Topic 3',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'It depends who\u2019s watching',
        body:
          'How fast something is moving depends on who\u2019s observing. If you walk down a moving train at 1 m/s and the train is going at 30 m/s relative to the ground, your speed relative to the ground is 30 + 1 = 31 m/s. If you walk the OTHER way (back down the train), your speed relative to the ground is 30 − 1 = 29 m/s. The same person, three different speeds depending on the observer\u2019s frame of reference.',
        check: {
          question: 'You walk at 1 m/s in the same direction as a 30 m/s train. Your speed relative to the ground?',
          options: ['1 m/s', '29 m/s', '30 m/s', '31 m/s'],
          answer: '31 m/s',
          explainer: 'Add the speeds because you are going the same direction.',
        },
      },
      {
        heading: 'Relative speed when objects move toward each other',
        body:
          'Two cars driving towards each other at 20 m/s and 30 m/s have a relative speed of 50 m/s — they are closing in on each other at 50 m/s. If they are travelling in the SAME direction, the relative speed is 30 − 20 = 10 m/s — the faster car is catching up at 10 m/s.',
        check: {
          question: 'A car at 25 m/s overtakes a car at 20 m/s. Relative speed?',
          options: ['5 m/s', '20 m/s', '25 m/s', '45 m/s'],
          answer: '5 m/s',
          explainer: 'Same direction → subtract: 25 − 20 = 5 m/s.',
        },
      },
      {
        heading: 'Why it matters',
        body:
          'Relative motion explains everyday weird-looking events. A passenger on a train sees the world rushing past, even though they "feel still" inside. A bird flying inside a moving plane stays at the bird\u2019s normal speed relative to the air inside the plane. Understanding frames of reference is also essential for physics like Doppler shift and (later) Einstein\u2019s relativity.',
        check: {
          question: 'You drop a ball inside a moving train. Where does it land relative to YOU?',
          options: ['Behind you', 'In front of you', 'Straight at your feet', 'Out of the window'],
          answer: 'Straight at your feet',
          explainer: 'You and the ball are both moving at the same speed as the train — relative to you, the ball just falls straight down.',
        },
      },
    ],
    keyPoints: [
      'Speed depends on the observer\u2019s frame of reference',
      'Same direction → ADD speeds',
      'Opposite directions → SUBTRACT (or add to find closing speed when approaching)',
      'A dropped ball inside a train falls "straight down" relative to you',
      'Frame of reference affects what you see',
    ],
    workedExamples: [
      {
        problem: 'Train moving east at 30 m/s. Sarah walks west along the carriage at 1.5 m/s. Find her speed relative to the ground.',
        steps: [
          { step: 1, instruction: 'Identify directions', working: 'Train east at 30 m/s; Sarah west at 1.5 m/s relative to train' },
          { step: 2, instruction: 'Combine', working: 'Opposite directions → 30 − 1.5 = 28.5 m/s east' },
        ],
        answer: '28.5 m/s east (relative to the ground).',
        commonMistake: 'Forgetting to keep track of direction. Always note east/west or +/−.',
      },
    ],
    whyItWorks:
      'There is no special "true" speed in physics — every measurement is relative to something else. That insight is at the heart of Einstein\u2019s relativity, where even time depends on the observer.',
  },
  glossary: [
    { term: 'Relative speed', definition: 'The speed of one object as seen from another moving object.' },
    { term: 'Frame of reference', definition: 'The viewpoint from which motion is measured.' },
  ],
  examQuestion: {
    prompt:
      'A car drives east at 25 m/s. A second car drives west at 15 m/s. (a) Calculate the speed of car 2 relative to car 1. (b) Explain why this is greater than either of their actual speeds. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Speeds add when moving in opposite directions',
      '25 + 15 = 40 m/s',
      'They are approaching each other, closing the gap',
      'so observer in car 1 sees car 2 coming at the COMBINED speed',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.3.w1', type: 'choice', question: 'You walk forward in a train at 1 m/s. Train moves at 30 m/s. Your speed relative to the ground?', options: ['1 m/s', '30 m/s', '31 m/s', '29 m/s'], answer: '31 m/s', hint1: 'Same direction → add', hint2: '30 + 1', hint3: 'Two digits' },
      { id: 'phys-9.3.w2', type: 'choice', question: 'Two cars approach each other at 20 m/s each. Closing speed?', options: ['0 m/s', '20 m/s', '40 m/s', '10 m/s'], answer: '40 m/s', hint1: 'Add when approaching', hint2: '20 + 20', hint3: 'Two digits' },
      { id: 'phys-9.3.w3', type: 'choice', question: 'Frame of reference means…', options: ['Picture frame', 'A viewpoint from which motion is measured', 'A car frame', 'A bicycle'], answer: 'A viewpoint from which motion is measured', hint1: 'Observer\u2019s perspective', hint2: 'Where you measure from', hint3: 'Linked to who is watching' },
    ],
    level1: [
      { id: 'phys-9.3.1', type: 'choice', question: 'Same direction speeds combine by…', options: ['Multiplying', 'Subtracting', 'Adding', 'Dividing'], answer: 'Adding', hint1: 'Walk forward in train', hint2: '30 + 1', hint3: 'Total motion same way' },
      { id: 'phys-9.3.2', type: 'number', question: 'Car at 30 m/s overtakes car at 25 m/s. Relative speed?', answer: '5', hint1: 'Same direction → subtract', hint2: '30 − 25', hint3: 'Single digit' },
    ],
    level2: [
      { id: 'phys-9.3.3', type: 'choice', question: 'Inside a smooth-moving train you drop a ball. It lands…', options: ['Behind you', 'In front of you', 'At your feet', 'Out of the window'], answer: 'At your feet', hint1: 'You and ball both moving at train speed', hint2: 'Relative to you, ball just falls', hint3: 'Same frame of reference' },
      { id: 'phys-9.3.4', type: 'number', question: 'Ship east at 5 m/s. Wind blows east at 3 m/s. Ship\u2019s speed relative to wind?', answer: '2', hint1: 'Same direction subtract', hint2: '5 − 3', hint3: 'Single digit' },
    ],
    level3: [
      { id: 'phys-9.3.5', type: 'choice', question: 'Why do raindrops appear to streak forward through the windscreen of a moving car?', options: ['Rain falls forward', 'Rain falls vertically; the car moves forward', 'Air resistance', 'Curved windscreen'], answer: 'Rain falls vertically; the car moves forward', hint1: 'Combination of motions', hint2: 'Relative to car', hint3: 'Two-direction motion combine' },
      { id: 'phys-9.3.6', type: 'text', question: 'When two objects move in opposite directions, the relative speed is found by ___ ing the two speeds.', answer: 'add', hint1: '3 letters', hint2: 'Combining', hint3: 'Plus' },
    ],
  },
};

// =====================================================================
// 9.4  FORCES AND MOVEMENT
// =====================================================================
const FORCES_TOPIC = {
  id: 'phys-9.4',
  title: 'Forces and Movement',
  slug: 'forces-and-movement',
  pageRef: 'Section 9 · Topic 4',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is a force?',
        body:
          'A force is a push or a pull. Forces have BOTH a size and a direction (so they are vectors). The unit is the newton (N). 1 N is roughly the weight of a small apple. Forces can change the speed, the direction, or even the SHAPE of an object.',
        check: {
          question: 'The unit of force is the…',
          options: ['Joule', 'Watt', 'Newton', 'Pascal'],
          answer: 'Newton',
          explainer: 'Named after Isaac Newton, who worked out the laws of motion.',
        },
      },
      {
        heading: 'Balanced and unbalanced',
        body:
          'When forces on an object are BALANCED (equal and opposite), the object stays at rest, OR keeps moving at a constant speed in a straight line. When forces are UNBALANCED (one bigger than the other), the object accelerates in the direction of the bigger force — speeds up, slows down, or changes direction.',
        check: {
          question: 'A car drives at constant 30 m/s in a straight line. The forces on it must be…',
          options: ['Balanced', 'Unbalanced', 'Zero', 'Weight only'],
          answer: 'Balanced',
          explainer: 'No acceleration → forces balanced (engine force matches friction + air resistance).',
        },
      },
      {
        heading: 'Resultant force',
        body:
          'When two forces act in a line, the resultant (net) force is what\u2019s left after they are added. Forces in the SAME direction → add. Forces in OPPOSITE directions → subtract, with the leftover pointing in the direction of the larger one. Resultant force = mass × acceleration (Newton\u2019s 2nd law). 0 resultant = no acceleration.',
        check: {
          question: 'A box is pushed left with 30 N and right with 50 N. Resultant?',
          options: ['20 N right', '20 N left', '80 N right', '0 N'],
          answer: '20 N right',
          explainer: '50 − 30 = 20 N net to the right.',
        },
      },
    ],
    keyPoints: [
      'Force = push or pull; vector (size + direction)',
      'Unit: newton (N)',
      'Balanced forces → no change in motion',
      'Unbalanced forces → acceleration in direction of bigger force',
      'F = m × a (resultant force = mass × acceleration)',
    ],
    workedExamples: [
      {
        problem: 'A 5 kg trolley is pushed with 50 N to the right and friction is 10 N to the left. Find the resultant force AND the acceleration.',
        steps: [
          { step: 1, instruction: 'Resultant', working: '50 − 10 = 40 N to the right' },
          { step: 2, instruction: 'Apply F = ma', working: '40 = 5 × a → a = 8 m/s²' },
        ],
        answer: 'Resultant 40 N right; acceleration 8 m/s² right.',
        commonMistake: 'Forgetting the units. Force in N, mass in kg → acceleration in m/s².',
      },
    ],
    whyItWorks:
      'Forces are how we explain every change in motion. Newton spotted the simple rule that resultant force = mass × acceleration — that one equation is the foundation of every motion problem in GCSE.',
  },
  glossary: [
    { term: 'Force', definition: 'A push or pull on an object; measured in newtons (N).' },
    { term: 'Newton (N)', definition: 'The SI unit of force; 1 N is roughly the weight of a small apple.' },
    { term: 'Balanced forces', definition: 'Equal and opposite forces — no acceleration.' },
    { term: 'Unbalanced forces', definition: 'A net force exists — the object accelerates.' },
    { term: 'Resultant force', definition: 'The single equivalent force after adding/subtracting all the forces on an object.' },
  ],
  examQuestion: {
    prompt:
      'A 1000 kg car experiences 4500 N forwards from the engine and 1500 N backwards from friction. (a) Find the resultant force. (b) Calculate the acceleration. (c) State Newton\u2019s 2nd law in words. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Resultant = 4500 − 1500 = 3000 N forwards',
      'a = F ÷ m = 3000 ÷ 1000 = 3 m/s²',
      'Newton\u2019s 2nd law: resultant force = mass × acceleration',
      '(higher resultant = bigger acceleration)',
      '(more massive object = smaller acceleration for same force)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.4.w1', type: 'text', question: 'The unit of force is the ___ .', answer: 'newton', hint1: '6 letters', hint2: 'Named after a scientist', hint3: 'N' },
      { id: 'phys-9.4.w2', type: 'choice', question: 'Balanced forces mean…', options: ['Acceleration', 'No change in motion', 'Speeding up', 'Slowing down'], answer: 'No change in motion', hint1: 'Stay at rest or constant speed', hint2: 'Net force = 0', hint3: 'No acceleration' },
      { id: 'phys-9.4.w3', type: 'number', question: 'Push right 60 N, friction left 20 N. Resultant (N)?', answer: '40', hint1: '60 − 20', hint2: 'Two digits', hint3: 'Right' },
    ],
    level1: [
      { id: 'phys-9.4.1', type: 'choice', question: 'When forces are unbalanced, the object…', options: ['Stays still', 'Accelerates', 'Heats up', 'Floats'], answer: 'Accelerates', hint1: 'Direction of bigger force', hint2: 'Newton 2', hint3: 'Speed/direction changes' },
      { id: 'phys-9.4.2', type: 'number', question: 'F = ma. F = 200 N, m = 50 kg. a (m/s²)?', answer: '4', hint1: 'Divide', hint2: 'Single digit', hint3: '200 ÷ 50' },
    ],
    level2: [
      { id: 'phys-9.4.3', type: 'choice', question: 'A 2 kg trolley accelerates at 3 m/s². Resultant force?', options: ['1 N', '5 N', '6 N', '0.67 N'], answer: '6 N', hint1: 'F = ma', hint2: '2 × 3', hint3: 'Single digit' },
      { id: 'phys-9.4.4', type: 'choice', question: 'A car drives at constant speed in a straight line. Forces are…', options: ['Balanced', 'Unbalanced', 'Zero', 'Backwards'], answer: 'Balanced', hint1: 'No acceleration', hint2: 'Engine = friction', hint3: 'Net = 0' },
    ],
    level3: [
      { id: 'phys-9.4.5', type: 'choice', question: 'Doubling resultant force on the same mass will…', options: ['Halve acceleration', 'Double acceleration', 'Quadruple acceleration', 'No effect'], answer: 'Double acceleration', hint1: 'Linear in F', hint2: 'F = ma', hint3: 'a doubles' },
      { id: 'phys-9.4.6', type: 'text', question: 'A push or pull is a ___ .', answer: 'force', hint1: '5 letters', hint2: 'Topic title', hint3: 'Starts with f' },
    ],
  },
};

// =====================================================================
// 9.5  FRICTION
// =====================================================================
const FRICTION_TOPIC = {
  id: 'phys-9.5',
  title: 'Friction',
  slug: 'friction',
  pageRef: 'Section 9 · Topic 5',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is friction?',
        body:
          'Friction is a force that OPPOSES motion when two surfaces rub. It always acts in the OPPOSITE direction to the way the object is moving (or trying to move). Two main types: STATIC friction (stops a stationary object from starting to move) and DYNAMIC friction (slows a moving object down). Microscopically, even smooth-looking surfaces have tiny bumps that catch on each other.',
        check: {
          question: 'Friction always acts…',
          options: ['In the direction of motion', 'Opposite to the direction of motion', 'Up only', 'Randomly'],
          answer: 'Opposite to the direction of motion',
          explainer: 'Friction tries to stop the object — it opposes movement.',
        },
      },
      {
        heading: 'Useful and unwanted',
        body:
          'Friction is sometimes USEFUL: shoes grip the ground (no friction = slip), brakes slow cars by friction in the brake pad, writing with a pencil. Sometimes UNWANTED: in engines, friction wastes energy as heat and wears parts down. Solutions: lubricate (oil reduces friction), polish surfaces, or use ball bearings. Aerodynamic shapes also reduce air drag (covered next).',
        check: {
          question: 'Why is oil added to bicycle chains?',
          options: ['To make them shiny', 'To reduce friction between metal parts', 'To increase friction', 'For decoration'],
          answer: 'To reduce friction between metal parts',
          explainer: 'Oil acts as a lubricant — fewer wasted joules of energy as heat and less wear.',
        },
      },
      {
        heading: 'Friction and energy',
        body:
          'Wherever there is friction, kinetic energy is being transferred to thermal energy — things heat up. Rub your hands together fast and they get warm. Brakes can become red-hot during heavy braking. This is why a "perfect" perpetual-motion machine is impossible — friction always slowly dissipates energy to the surroundings.',
        check: {
          question: 'Pushing a heavy box across the floor, the friction transfers some of the kinetic energy of the box into…',
          options: ['Light', 'Sound only', 'Heat (in the surfaces)', 'Nothing'],
          answer: 'Heat (in the surfaces)',
          explainer: 'Friction always heats up the surfaces in contact.',
        },
      },
    ],
    keyPoints: [
      'Friction = force opposing motion between two surfaces',
      'Static friction stops things from starting; dynamic from continuing',
      'Useful: grip, brakes, writing',
      'Unwanted: engine wear, energy wasted as heat',
      'Reduce friction: lubrication, polishing, ball bearings',
      'Friction transfers kinetic energy → thermal',
    ],
    workedExamples: [
      {
        problem: 'A 100 N push on a heavy box just keeps it sliding at constant speed. Find the friction force on the box.',
        steps: [
          { step: 1, instruction: 'Constant speed', working: 'Forces balanced' },
          { step: 2, instruction: 'Therefore', working: 'Friction = push = 100 N (opposite direction)' },
        ],
        answer: 'Friction = 100 N (opposite to the push).',
        commonMistake: 'Saying friction is greater than the push because the box is heavy. At constant speed, friction EQUALS the push.',
      },
    ],
    whyItWorks:
      'Friction is always there in the real world. Designers either embrace it (brake pads, tyres) or fight it (engines, bearings). Either way it converts useful kinetic energy to wasted heat.',
  },
  glossary: [
    { term: 'Friction', definition: 'A force that opposes motion between two touching surfaces.' },
    { term: 'Static friction', definition: 'Friction stopping a stationary object from starting to move.' },
    { term: 'Dynamic friction', definition: 'Friction acting on an already-moving object.' },
    { term: 'Lubricant', definition: 'A substance (e.g. oil) that reduces friction between surfaces.' },
  ],
  examQuestion: {
    prompt:
      "A girl pushes a 30 N box with 30 N force. The box doesn't move. (a) State the size and direction of the friction force. (b) Explain why oil is added to a car's engine. (c) State an example of USEFUL friction. (5 marks)",
    maxMarks: 5,
    markSchemePoints: [
      'Friction = 30 N (opposite to the push)',
      'Oil reduces friction between moving metal parts',
      'so less energy is wasted as heat / less wear over time',
      'Brakes / shoes gripping floor / tyre tread / pencil on paper (any sensible)',
      '(Plus a written reason that this friction is desired)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.5.w1', type: 'text', question: 'A force opposing motion between two surfaces is called ___ .', answer: 'friction', hint1: '8 letters', hint2: 'Topic title', hint3: 'Starts with f' },
      { id: 'phys-9.5.w2', type: 'choice', question: 'Friction always acts…', options: ['Forward', 'Up', 'Opposite to motion', 'Down only'], answer: 'Opposite to motion', hint1: 'Resists movement', hint2: 'Three words', hint3: 'Backwards on a moving object' },
      { id: 'phys-9.5.w3', type: 'choice', question: 'Friction transfers kinetic energy mostly into…', options: ['Light', 'Sound', 'Heat', 'Mass'], answer: 'Heat', hint1: 'Things warm up', hint2: '4 letters', hint3: 'Thermal' },
    ],
    level1: [
      { id: 'phys-9.5.1', type: 'choice', question: 'Why oil bicycle chains?', options: ['Looks nice', 'Reduce friction', 'Increase grip', 'Add weight'], answer: 'Reduce friction', hint1: 'Less energy wasted', hint2: 'Smoother', hint3: 'Less wear' },
      { id: 'phys-9.5.2', type: 'choice', question: 'Useful friction example?', options: ['Engine wear', 'Brake pads slowing a car', 'Heat loss in a fridge', 'Bearings'], answer: 'Brake pads slowing a car', hint1: 'Friction needed for stopping', hint2: 'Pad on disc', hint3: 'Useful' },
    ],
    level2: [
      { id: 'phys-9.5.3', type: 'choice', question: 'Pushing a 50 N box at constant speed needs 50 N of push. Friction force is…', options: ['25 N', '50 N', '100 N', '0 N'], answer: '50 N', hint1: 'Forces balanced', hint2: 'Equal and opposite', hint3: 'Same as push' },
      { id: 'phys-9.5.4', type: 'choice', question: 'Why do skis have wax?', options: ['Stops melting', 'Reduces friction with snow', 'Makes them brighter', 'Adds weight'], answer: 'Reduces friction with snow', hint1: 'Slide faster', hint2: 'Less drag', hint3: 'Smooth surface' },
    ],
    level3: [
      { id: 'phys-9.5.5', type: 'choice', question: 'Without ANY friction, walking would be…', options: ['Easy', 'Impossible (you would slip)', 'The same', 'Faster'], answer: 'Impossible (you would slip)', hint1: 'Need grip', hint2: 'Foot push', hint3: 'No traction' },
      { id: 'phys-9.5.6', type: 'text', question: 'A substance like oil that reduces friction is called a ___ .', answer: 'lubricant', hint1: '9 letters', hint2: 'Reduces wear', hint3: 'Starts with l' },
    ],
  },
};

// =====================================================================
// 9.6  AIR AND WATER RESISTANCE
// =====================================================================
const RESISTANCE_TOPIC = {
  id: 'phys-9.6',
  title: 'Air and Water Resistance',
  slug: 'air-and-water-resistance',
  pageRef: 'Section 9 · Topic 6',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Drag forces',
        body:
          'Air resistance (drag) and water resistance are friction-like forces between an object and the FLUID (gas or liquid) it is moving through. Both ALWAYS oppose motion. Bigger when the object is faster, larger in cross-section, or in a denser fluid. That\u2019s why falling leaves drift slowly while a rock falls fast — bigger leaf area = more air resistance.',
        check: {
          question: 'Air resistance on a moving object…',
          options: ['Helps it speed up', 'Always opposes motion', 'Acts upwards only', 'Acts horizontally only'],
          answer: 'Always opposes motion',
          explainer: 'It\u2019s a drag force; works against whichever way you\u2019re going.',
        },
      },
      {
        heading: 'Streamlining',
        body:
          'Vehicles, fish, planes and birds all use streamlined shapes to cut down drag. A teardrop shape lets the fluid flow smoothly past — much less resistance than a flat block. Modern cars are designed in wind tunnels for this reason; cyclists wear tight clothing and aero helmets; submarines and torpedoes are shaped like teardrops too.',
        check: {
          question: 'Why are cars designed with smooth, curved shapes?',
          options: ['Look nicer', 'Reduce air resistance and save fuel', 'Heavier', 'Cheaper'],
          answer: 'Reduce air resistance and save fuel',
          explainer: 'Less drag = less wasted fuel energy.',
        },
      },
      {
        heading: 'Terminal velocity',
        body:
          'A skydiver leaves the plane and accelerates downwards (gravity is bigger than air resistance). As they speed up, air resistance grows. Eventually, air resistance EQUALS gravity — net force = 0 — and they fall at constant speed. This is terminal velocity. Opening a parachute sharply increases air resistance; the skydiver decelerates until they reach a NEW (much slower) terminal velocity, safe for landing.',
        check: {
          question: 'At terminal velocity, the forces on a skydiver are…',
          options: ['Unbalanced (still accelerating)', 'Balanced (constant speed)', 'Both zero', 'Just gravity'],
          answer: 'Balanced (constant speed)',
          explainer: 'Air resistance = gravity, so net force = 0 → constant speed.',
        },
      },
    ],
    keyPoints: [
      'Air/water resistance = drag, opposing motion through a fluid',
      'Bigger if: faster, larger cross-section, denser fluid',
      'Streamlining (teardrop shape) reduces drag',
      'Terminal velocity reached when drag = gravity',
      'Parachute increases drag → much slower terminal velocity',
    ],
    workedExamples: [
      {
        problem: 'A skydiver in free-fall reaches terminal velocity. They then open the parachute. Describe what happens to (i) air resistance, (ii) net force, (iii) speed, in the few seconds afterwards.',
        steps: [
          { step: 1, instruction: 'Air resistance', working: 'Drops? No — actually JUMPS up because of huge surface area' },
          { step: 2, instruction: 'Net force', working: 'Now air resistance > weight → upward net force' },
          { step: 3, instruction: 'Speed', working: 'Decelerates rapidly until air resistance falls back to equal weight at the new (slower) terminal velocity' },
        ],
        answer: 'Air resistance increases sharply; net force points upwards; skydiver slows down; new (smaller) terminal velocity reached.',
        commonMistake: 'Saying speed becomes zero when the parachute opens — they only DECELERATE to a slower constant speed, not stop in mid-air!',
      },
    ],
    whyItWorks:
      'Drag is the price you pay for moving through a fluid. The bigger and faster you go, the bigger the price. That\u2019s why a sleek penguin or a teardrop racer cuts through their fluid with so much less effort than a brick.',
  },
  glossary: [
    { term: 'Air resistance', definition: 'A drag force on an object moving through air.' },
    { term: 'Water resistance', definition: 'A drag force on an object moving through water.' },
    { term: 'Streamlined', definition: 'Shaped to reduce drag (smooth, teardrop-like).' },
    { term: 'Terminal velocity', definition: 'The constant final speed when drag equals gravity (or driving force).' },
  ],
  examQuestion: {
    prompt:
      'A skydiver jumps from a plane. (a) Describe how their speed changes from when they jump to when they reach terminal velocity. (b) Explain what happens when they open their parachute. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'At first, gravity > air resistance → net force down → speed up (acceleration)',
      'As speed rises, air resistance grows',
      'At terminal velocity, air resistance = gravity → constant speed',
      'Opening parachute massively increases air resistance',
      'Air resistance > gravity → upward net force → decelerate to new (slower) terminal velocity',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.6.w1', type: 'text', question: 'Drag from air on a moving object is called air ___ .', answer: 'resistance', hint1: '10 letters', hint2: 'Topic title bit', hint3: 'Starts with r' },
      { id: 'phys-9.6.w2', type: 'choice', question: 'Streamlining helps because…', options: ['Looks fast', 'Reduces drag', 'Increases drag', 'Heats fluid'], answer: 'Reduces drag', hint1: 'Smooth flow', hint2: 'Less resistance', hint3: 'Saves fuel' },
      { id: 'phys-9.6.w3', type: 'choice', question: 'At terminal velocity, forces on a skydiver are…', options: ['Unbalanced', 'Balanced', 'Zero', 'Increasing'], answer: 'Balanced', hint1: 'Constant speed', hint2: 'Net = 0', hint3: 'Equilibrium' },
    ],
    level1: [
      { id: 'phys-9.6.1', type: 'choice', question: 'Drag GROWS when an object is…', options: ['Slower', 'Smaller', 'Faster', 'Lighter'], answer: 'Faster', hint1: 'Speed dependence', hint2: 'Big at high speed', hint3: 'Greater drag at greater v' },
      { id: 'phys-9.6.2', type: 'text', question: 'A teardrop shape is described as ___ .', answer: 'streamlined', hint1: '11 letters', hint2: 'Reduces drag', hint3: 'Starts with s' },
    ],
    level2: [
      { id: 'phys-9.6.3', type: 'choice', question: 'A heavy ball and a feather are dropped on the Moon (no air). Which lands first?', options: ['Heavy ball', 'Feather', 'They land together', 'Neither'], answer: 'They land together', hint1: 'No air resistance on Moon', hint2: 'Same gravity', hint3: 'Same acceleration' },
      { id: 'phys-9.6.4', type: 'choice', question: 'Opening a parachute first causes…', options: ['Acceleration upwards', 'Deceleration to a slower terminal velocity', 'Stops in mid-air', 'No effect'], answer: 'Deceleration to a slower terminal velocity', hint1: 'Drag suddenly bigger', hint2: 'Net force up', hint3: 'New equilibrium' },
    ],
    level3: [
      { id: 'phys-9.6.5', type: 'choice', question: 'Why do submarines have a teardrop shape?', options: ['It looks cool', 'Reduce water resistance / drag', 'Hold more crew', 'Strong against pressure only'], answer: 'Reduce water resistance / drag', hint1: 'Streamlining underwater', hint2: 'Same idea as cars', hint3: 'Glide through water' },
      { id: 'phys-9.6.6', type: 'text', question: 'Constant final speed reached when drag = weight is called ___ velocity.', answer: 'terminal', hint1: '8 letters', hint2: 'Final speed', hint3: 'Starts with t' },
    ],
  },
};

// =====================================================================
// 9.7  FORCE DIAGRAMS
// =====================================================================
const DIAGRAMS_TOPIC = {
  id: 'phys-9.7',
  title: 'Force Diagrams',
  slug: 'force-diagrams',
  pageRef: 'Section 9 · Topic 7',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Drawing forces',
        body:
          'A force diagram is a sketch of an object with arrows showing every force acting on it. Each arrow starts FROM the object, points in the direction of the force, and is labelled with the size in newtons. Bigger forces = longer arrows. Forces always come in pairs: an object on a table feels gravity DOWN and a normal contact force UP from the table.',
        check: {
          question: 'A book on a table experiences which TWO main forces?',
          options: ['Weight + push', 'Weight + tension', 'Weight + normal contact force', 'Weight + air'],
          answer: 'Weight + normal contact force',
          explainer: 'Gravity pulls the book down; the table pushes it up with the same force.',
        },
      },
      {
        heading: 'Common force types',
        body:
          'Weight (gravity) — always downward; equal to mass × gravitational field strength (W = m × g, with g = 10 N/kg on Earth). Normal contact force — push from a surface, perpendicular to it. Tension — pull along a rope/string. Friction — opposes motion along the surface. Air/water resistance — opposes motion through a fluid. Driving force — engine push, etc.',
        check: {
          question: 'A 5 kg ball on Earth has weight…',
          options: ['5 N', '10 N', '50 N', '500 N'],
          answer: '50 N',
          explainer: 'W = m × g = 5 × 10 = 50 N.',
        },
      },
      {
        heading: 'Resultant from a diagram',
        body:
          'To find the resultant force from a force diagram in 1D: forces in the same direction → add. Forces in opposite directions → subtract, leftover is the resultant. If forces are equal and opposite (balanced), the resultant is 0 → no acceleration. If unbalanced → the object accelerates in the direction of the resultant.',
        check: {
          question: 'A car has 2000 N driving force forwards and 800 N drag backwards. Resultant?',
          options: ['1200 N forwards', '2800 N forwards', '800 N backwards', '0 N'],
          answer: '1200 N forwards',
          explainer: '2000 − 800 = 1200 N forwards.',
        },
      },
    ],
    keyPoints: [
      'Force diagram = arrows showing each force on an object',
      'Arrow starts from object; longer = bigger',
      'Common forces: weight, normal contact, tension, friction, drag, driving',
      'Weight = mass × gravity (10 N/kg on Earth)',
      'Resultant = add same-direction; subtract opposite-direction',
    ],
    workedExamples: [
      {
        problem: 'Sketch the forces on a 2 kg block sliding right across a rough surface, given a 10 N push to the right and 4 N friction. Find the resultant force.',
        steps: [
          { step: 1, instruction: 'Weight', working: '2 × 10 = 20 N down' },
          { step: 2, instruction: 'Normal force', working: '20 N up (balances weight)' },
          { step: 3, instruction: 'Push', working: '10 N right' },
          { step: 4, instruction: 'Friction', working: '4 N left' },
          { step: 5, instruction: 'Resultant', working: '10 − 4 = 6 N right' },
        ],
        answer: '6 N to the right.',
        commonMistake: 'Forgetting to draw both vertical forces. They balance but you must show them.',
      },
    ],
    whyItWorks:
      'Force diagrams turn a wordy physics problem into a clear picture. Once every force is drawn, finding the resultant is straightforward arithmetic. Most marks in a 5-mark question come from drawing the diagram correctly.',
  },
  glossary: [
    { term: 'Force diagram', definition: 'A sketch showing all the forces acting on an object as labelled arrows.' },
    { term: 'Weight', definition: 'The force of gravity on an object: W = m × g.' },
    { term: 'Normal contact force', definition: 'The force a surface exerts on an object, perpendicular to the surface.' },
    { term: 'Tension', definition: 'The pulling force in a rope, string or cable.' },
  ],
  examQuestion: {
    prompt:
      'A 4 kg crate sits on a horizontal table. Someone pulls with 25 N to the right. Friction on the crate is 5 N. (a) Sketch a force diagram. (b) Calculate weight. (c) Find the resultant force in the horizontal direction. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Diagram shows 4 forces: weight down, normal force up, pull right, friction left (1 mark each up to 2 marks)',
      'Weight = 4 × 10 = 40 N',
      'Normal force = 40 N (balances weight)',
      'Resultant horizontal = 25 − 5 = 20 N right',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.7.w1', type: 'text', question: 'On Earth, gravity strength is about ___ N/kg.', answer: '10', hint1: 'Two digits', hint2: 'Round number', hint3: 'Used in W = mg' },
      { id: 'phys-9.7.w2', type: 'number', question: 'Weight of 3 kg on Earth (N)?', answer: '30', hint1: 'm × g', hint2: '3 × 10', hint3: 'Two digits' },
      { id: 'phys-9.7.w3', type: 'choice', question: 'A book on a table — table pushes…', options: ['Down', 'Up (normal force)', 'Sideways', 'Inwards'], answer: 'Up (normal force)', hint1: 'Reaction to weight', hint2: 'Perpendicular to surface', hint3: 'Holds the book up' },
    ],
    level1: [
      { id: 'phys-9.7.1', type: 'choice', question: 'Tension is the force in…', options: ['A spring being stretched', 'A rope pulling something', 'Solid floor', 'A solid magnet'], answer: 'A rope pulling something', hint1: 'Pulled rope', hint2: 'Pulling force', hint3: 'Carries load' },
      { id: 'phys-9.7.2', type: 'number', question: 'Push 100 N right, friction 30 N left. Resultant (N)?', answer: '70', hint1: '100 − 30', hint2: 'Two digits', hint3: 'Right' },
    ],
    level2: [
      { id: 'phys-9.7.3', type: 'choice', question: 'On a force diagram, longer arrows mean…', options: ['Bigger force', 'Slower', 'Lighter object', 'Higher up'], answer: 'Bigger force', hint1: 'Length scales with size', hint2: 'Length = magnitude', hint3: 'Visualise size' },
      { id: 'phys-9.7.4', type: 'number', question: '6 kg object — weight on Earth (N)?', answer: '60', hint1: 'm × g', hint2: '6 × 10', hint3: 'Two digits' },
    ],
    level3: [
      { id: 'phys-9.7.5', type: 'choice', question: 'A 2 kg apple is in free-fall (no air). Net force on it is…', options: ['0 N', '10 N down', '20 N down', '20 N up'], answer: '20 N down', hint1: 'Just weight', hint2: 'No air resistance', hint3: 'm × g' },
      { id: 'phys-9.7.6', type: 'text', question: 'The push from a surface on an object, perpendicular to the surface, is the ___ force.', answer: 'normal', hint1: '6 letters', hint2: 'Right-angles', hint3: 'Starts with n' },
    ],
  },
};

// =====================================================================
// 9.8  MOMENTS
// =====================================================================
const MOMENTS_TOPIC = {
  id: 'phys-9.8',
  title: 'Moments',
  slug: 'moments',
  pageRef: 'Section 9 · Topic 8',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'The turning effect of a force',
        body:
          'A moment is the turning effect of a force around a pivot. Formula: moment = force × perpendicular distance from the pivot. Units: N m (newton metres). The bigger the force OR the bigger the distance, the bigger the moment. That\u2019s why long-handled spanners loosen tight nuts — more leverage.',
        check: {
          question: 'A 10 N force is applied 0.5 m from a pivot. Moment?',
          options: ['2 N m', '5 N m', '10 N m', '20 N m'],
          answer: '5 N m',
          explainer: '10 × 0.5 = 5 N m.',
        },
      },
      {
        heading: 'Balance and the principle of moments',
        body:
          "An object is balanced (in rotational equilibrium) when the TOTAL clockwise moments equal the TOTAL anti-clockwise moments around the same pivot. This is the principle of moments. It's how a see-saw works: a heavier child sits closer to the pivot to balance a lighter child sitting further away.",
        check: {
          question: 'A 200 N child sits 1.5 m from the pivot. Where must a 300 N child sit to balance them on a see-saw?',
          options: ['0.5 m', '1.0 m', '1.5 m', '2.0 m'],
          answer: '1.0 m',
          explainer: '200 × 1.5 = 300 × d, so d = 300 ÷ 300 = 1.0 m.',
        },
      },
      {
        heading: 'Levers and gears',
        body:
          'Levers are simple machines that use moments to multiply force. A long crowbar lets you lift a heavy box with a small effort because the effort distance is much greater than the load distance. Spanners, scissors, wheelbarrows and even your forearm at the elbow are levers. Gears use the same principle — small gears turn fast with low force, big gears turn slow with high force.',
        check: {
          question: 'Why do crowbars have long handles?',
          options: ['Weight', 'Bigger moment from a small effort force', 'Cost', 'Looks'],
          answer: 'Bigger moment from a small effort force',
          explainer: 'Long distance × small force = big moment, lifting big loads.',
        },
      },
    ],
    keyPoints: [
      'Moment = force × perpendicular distance from pivot',
      'Units: N m',
      'Principle of moments: clockwise moments = anti-clockwise moments when balanced',
      'Levers multiply force using long handles',
      'Bigger distance OR bigger force → bigger moment',
    ],
    workedExamples: [
      {
        problem: 'A see-saw has a 250 N child sitting 2 m from the pivot. A 400 N adult sits on the other side. How far from the pivot must the adult be for the see-saw to balance?',
        steps: [
          { step: 1, instruction: 'Apply principle', working: 'CW moment = ACW moment' },
          { step: 2, instruction: 'Substitute', working: '400 × d = 250 × 2 = 500' },
          { step: 3, instruction: 'Solve', working: 'd = 500 ÷ 400 = 1.25 m' },
        ],
        answer: '1.25 m from the pivot.',
        commonMistake: 'Mixing up clockwise and anti-clockwise — it doesn\u2019t matter which is which, as long as one balances the other.',
      },
    ],
    whyItWorks:
      'Moments explain why long handles, big wheels and good leverage all work — they turn small forces into BIG turning effects. Master this and you understand most simple machines.',
  },
  glossary: [
    { term: 'Moment', definition: 'The turning effect of a force around a pivot. Moment = F × d.' },
    { term: 'Pivot', definition: 'The fixed point that an object rotates around.' },
    { term: 'Lever', definition: 'A simple machine that uses a pivot to multiply force.' },
    { term: 'Principle of moments', definition: 'For balance: total clockwise moments = total anti-clockwise moments.' },
  ],
  examQuestion: {
    prompt:
      'A spanner is used to loosen a nut. The handle is 30 cm long and a 25 N force is applied at the end, perpendicular to the handle. (a) Calculate the moment in N m. (b) Suggest two ways to make it easier to loosen a stiffer nut. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      '30 cm = 0.3 m',
      'Moment = 25 × 0.3 = 7.5 N m',
      'Use a longer spanner (bigger d)',
      'OR push harder (bigger F)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.8.w1', type: 'text', question: 'Moment = force × ___ from pivot.', answer: 'distance', hint1: '8 letters', hint2: 'Lever arm', hint3: 'Starts with d' },
      { id: 'phys-9.8.w2', type: 'number', question: '20 N × 2 m = moment (N m)?', answer: '40', hint1: 'Multiply', hint2: 'Two digits', hint3: 'Forty' },
      { id: 'phys-9.8.w3', type: 'choice', question: 'Units of moment?', options: ['N', 'N m', 'N/s', 'kg'], answer: 'N m', hint1: 'Force × distance', hint2: 'Two characters', hint3: 'Newton metres' },
    ],
    level1: [
      { id: 'phys-9.8.1', type: 'choice', question: 'For balance: total clockwise moment ___ total anti-clockwise moment.', options: ['Greater than', 'Less than', 'Equals', 'Doubles'], answer: 'Equals', hint1: 'Principle of moments', hint2: 'No net rotation', hint3: 'They balance' },
      { id: 'phys-9.8.2', type: 'number', question: 'Force = 5 N at 0.4 m from pivot. Moment?', answer: '2', hint1: '5 × 0.4', hint2: 'Single digit', hint3: 'Two' },
    ],
    level2: [
      { id: 'phys-9.8.3', type: 'choice', question: 'A child sits 1 m from a pivot creating 100 N m. Their weight?', options: ['10 N', '50 N', '100 N', '1000 N'], answer: '100 N', hint1: 'Moment ÷ distance', hint2: '100 ÷ 1', hint3: 'Three digits' },
      { id: 'phys-9.8.4', type: 'choice', question: 'Why long-handled spanners?', options: ['Cheaper', 'Bigger moment for same force', 'Smaller heads', 'Lighter'], answer: 'Bigger moment for same force', hint1: 'Larger d', hint2: 'F × d up', hint3: 'More leverage' },
    ],
    level3: [
      { id: 'phys-9.8.5', type: 'number', question: 'Child A 300 N at 1.5 m. Child B 450 N. Distance for B (m)?', answer: '1', hint1: 'CW = ACW: 300 × 1.5 = 450 × d', hint2: '450 ÷ 450', hint3: 'Single digit' },
      { id: 'phys-9.8.6', type: 'text', question: 'A simple machine using a pivot to multiply force is called a ___ .', answer: 'lever', hint1: '5 letters', hint2: 'Crowbar example', hint3: 'Starts with l' },
    ],
  },
};

// =====================================================================
// 9.9  FORCES AND ELASTICITY
// =====================================================================
const ELASTICITY_TOPIC = {
  id: 'phys-9.9',
  title: 'Forces and Elasticity',
  slug: 'forces-and-elasticity',
  pageRef: 'Section 9 · Topic 9',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Stretching and squashing',
        body:
          'Many objects deform (change shape) when forces act on them — they stretch (springs, rubber bands), compress (sponges, springs squashed), or bend (rulers). Elastic materials return to their original shape when the force is removed. Plastic materials stay deformed (e.g. play dough). Some materials are elastic up to a point, then deform plastically.',
        check: {
          question: 'A material that returns to its original shape after being stretched is…',
          options: ['Plastic', 'Elastic', 'Rigid', 'Liquid'],
          answer: 'Elastic',
          explainer: 'Elastic = bounces back to original shape.',
        },
      },
      {
        heading: 'Hooke\u2019s law',
        body:
          'For a spring within its elastic limit: extension is proportional to the force applied. Formula: F = k × x, where F is the force in N, x is the extension in m, and k is the spring constant in N/m. A stiffer spring has a bigger k. Doubling the force doubles the extension. If you stretch too far, the spring goes past its elastic limit and is permanently bent.',
        check: {
          question: 'A spring extends 2 cm under 4 N. Its spring constant?',
          options: ['0.5 N/m', '2 N/m', '50 N/m', '200 N/m'],
          answer: '200 N/m',
          explainer: 'k = F ÷ x = 4 ÷ 0.02 = 200 N/m. (Convert cm → m!)',
        },
      },
      {
        heading: 'Elastic limit',
        body:
          'Hooke\u2019s law only works UP TO the elastic limit. Beyond it, the relationship between force and extension is no longer linear (a graph curves), and the spring/rubber doesn\u2019t fully return when the force is removed — it stays stretched. On a force-extension graph, you see a straight line going through the origin until the elastic limit, then a curve.',
        check: {
          question: 'Beyond the elastic limit, a stretched spring will…',
          options: ['Return to original length perfectly', 'Stay permanently stretched', 'Disappear', 'Get colder'],
          answer: 'Stay permanently stretched',
          explainer: 'Past elastic limit = permanent deformation.',
        },
      },
    ],
    keyPoints: [
      'Elastic = returns to original shape; plastic = stays deformed',
      'Hooke\u2019s law: F = k × x (within elastic limit)',
      'k = spring constant (N/m); bigger k = stiffer spring',
      'Beyond elastic limit: relationship becomes non-linear',
      'Spring permanently stretched past elastic limit',
    ],
    workedExamples: [
      {
        problem: 'A spring with k = 50 N/m has a 6 N weight hung from it. Find the extension in cm.',
        steps: [
          { step: 1, instruction: 'Apply Hooke\u2019s law', working: 'F = k × x → x = F ÷ k' },
          { step: 2, instruction: 'Substitute', working: 'x = 6 ÷ 50 = 0.12 m' },
          { step: 3, instruction: 'Convert to cm', working: '0.12 m = 12 cm' },
        ],
        answer: '12 cm.',
        commonMistake: 'Mixing units — make sure F in N, x in m for k in N/m.',
      },
    ],
    whyItWorks:
      'Hooke\u2019s law explains why force meters (newtonmeters) work — a spring inside stretches in proportion to the load, so a calibrated scale shows the force. Push past the elastic limit, though, and the meter is ruined.',
  },
  glossary: [
    { term: 'Elastic', definition: 'Returns to original shape when force is removed.' },
    { term: 'Plastic deformation', definition: 'Permanent change of shape that does NOT return.' },
    { term: 'Hooke\u2019s law', definition: 'F = k × x: extension is proportional to force, within elastic limit.' },
    { term: 'Spring constant (k)', definition: 'How stiff a spring is, in N/m.' },
    { term: 'Elastic limit', definition: 'The point beyond which a spring no longer returns to its original length.' },
  ],
  examQuestion: {
    prompt:
      'A spring extends 8 cm when a 20 N weight is hung on it. (a) Calculate the spring constant. (b) Predict the extension when 30 N is hung on the same spring (assume still within the elastic limit). (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Convert: 8 cm = 0.08 m',
      'k = F ÷ x = 20 ÷ 0.08 = 250 N/m',
      'For 30 N: x = F ÷ k = 30 ÷ 250',
      '= 0.12 m = 12 cm',
      '(Within elastic limit assumed)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.9.w1', type: 'text', question: 'Hooke\u2019s law: F = k × ___ .', answer: 'x', hint1: '1 letter', hint2: 'Extension', hint3: 'Variable for stretch' },
      { id: 'phys-9.9.w2', type: 'choice', question: 'Spring constant has units of…', options: ['N', 'N/m', 'm', 'N m'], answer: 'N/m', hint1: 'Force per metre', hint2: 'Three characters', hint3: 'Stiffness measure' },
      { id: 'phys-9.9.w3', type: 'choice', question: 'Beyond elastic limit, a spring…', options: ['Returns to length', 'Stays permanently stretched', 'Becomes a liquid', 'Doubles in length'], answer: 'Stays permanently stretched', hint1: 'Permanent deformation', hint2: 'No bounce-back', hint3: 'Plastic region' },
    ],
    level1: [
      { id: 'phys-9.9.1', type: 'number', question: 'F = 10 N, x = 0.5 m. Spring constant k (N/m)?', answer: '20', hint1: 'F ÷ x', hint2: '10 ÷ 0.5', hint3: 'Two digits' },
      { id: 'phys-9.9.2', type: 'choice', question: 'A material that returns to shape after stretching is…', options: ['Plastic', 'Elastic', 'Brittle', 'Solid only'], answer: 'Elastic', hint1: 'Bouncy', hint2: 'Topic title', hint3: 'Starts with e' },
    ],
    level2: [
      { id: 'phys-9.9.3', type: 'number', question: 'k = 100 N/m, x = 0.05 m. Force F (N)?', answer: '5', hint1: 'k × x', hint2: '100 × 0.05', hint3: 'Single digit' },
      { id: 'phys-9.9.4', type: 'number', question: 'Spring stretches 3 cm under 6 N. k (N/m)?', answer: '200', hint1: 'Convert 3 cm = 0.03 m', hint2: '6 ÷ 0.03', hint3: 'Three digits' },
    ],
    level3: [
      { id: 'phys-9.9.5', type: 'choice', question: 'On a force-extension graph, Hooke\u2019s law shows up as…', options: ['A curve', 'A straight line through the origin', 'A horizontal line', 'A circle'], answer: 'A straight line through the origin', hint1: 'F ∝ x', hint2: 'Linear', hint3: 'Through (0,0)' },
      { id: 'phys-9.9.6', type: 'text', question: 'The spring constant tells you how ___ the spring is (bigger = stiffer).', answer: 'stiff', hint1: '5 letters', hint2: 'Hard to stretch', hint3: 'Starts with s' },
    ],
  },
};

// =====================================================================
// 9.10  PRESSURE
// =====================================================================
const PRESSURE_TOPIC = {
  id: 'phys-9.10',
  title: 'Pressure',
  slug: 'pressure',
  pageRef: 'Section 9 · Topic 10',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Force spread over an area',
        body:
          'Pressure measures how a force is spread over a surface. Formula: pressure = force ÷ area. Units: N/m² or pascals (Pa). 1 Pa = 1 N/m². The smaller the area, the bigger the pressure for the same force — that\u2019s why sharp knives cut and stiletto heels can damage floors. Snowshoes do the OPPOSITE — large area = low pressure → don\u2019t sink.',
        check: {
          question: 'A 100 N force on a 0.5 m² area gives a pressure of…',
          options: ['50 Pa', '200 Pa', '500 Pa', '0.005 Pa'],
          answer: '200 Pa',
          explainer: '100 ÷ 0.5 = 200 Pa.',
        },
      },
      {
        heading: 'Pressure in liquids and gases',
        body:
          'In a liquid, pressure increases with DEPTH. The deeper you go, the more water above pushing down. That\u2019s why submarines need thicker hulls deep in the ocean. Pressure also depends on the LIQUID\u2019S DENSITY — denser liquid = more pressure per metre. Atmospheric pressure (the air above us) is about 100,000 Pa (100 kPa) at sea level.',
        check: {
          question: 'Why does water pressure increase with depth?',
          options: ['Water gets heavier', 'More water weight pushing down on you', 'Less oxygen', 'Cold'],
          answer: 'More water weight pushing down on you',
          explainer: 'Each metre of water above adds extra weight, raising the pressure.',
        },
      },
      {
        heading: 'Real-world pressure',
        body:
          'Sharp blades, drawing pins, animal fangs — all use small contact area to make HIGH pressure. Wide tractor tyres, snowshoes, foundations of buildings — all spread weight over a LARGE area to make LOW pressure (so heavy things don\u2019t sink into soft ground). Hydraulic systems (car brakes, lifts) use the fact that pressure in a fluid is transmitted equally in all directions to multiply force.',
        check: {
          question: 'Why are tractor tyres so wide?',
          options: ['Looks tough', 'Spread weight → low pressure on soft ground', 'Cheaper', 'Last longer'],
          answer: 'Spread weight → low pressure on soft ground',
          explainer: 'Wide tyres = big area = low pressure → tractor doesn\u2019t sink into mud.',
        },
      },
    ],
    keyPoints: [
      'Pressure = force ÷ area',
      'Units: Pa = N/m²',
      'Smaller area = higher pressure',
      'Liquid pressure rises with depth and density',
      'Atmospheric pressure ≈ 100,000 Pa at sea level',
      'Sharp = high P; wide = low P',
    ],
    workedExamples: [
      {
        problem: 'A box weighs 600 N and sits on the floor with a 0.4 m² base. Find the pressure.',
        steps: [
          { step: 1, instruction: 'Formula', working: 'P = F ÷ A' },
          { step: 2, instruction: 'Substitute', working: '600 ÷ 0.4 = 1500 Pa' },
        ],
        answer: '1500 Pa.',
        commonMistake: 'Forgetting m² (squared) in the units of area. 0.4 m² is NOT the same as 0.4 m.',
      },
    ],
    whyItWorks:
      'Pressure is what links FORCE and AREA — and explains why a needle pricks but a finger doesn\u2019t, why dam walls are thicker at the bottom, and why your ears pop when scuba diving. The simple formula P = F/A is one of the most useful in physics.',
  },
  glossary: [
    { term: 'Pressure', definition: 'Force per unit area; P = F ÷ A.' },
    { term: 'Pascal (Pa)', definition: 'The SI unit of pressure. 1 Pa = 1 N/m².' },
    { term: 'Atmospheric pressure', definition: 'The pressure exerted by the air above us; about 100,000 Pa at sea level.' },
    { term: 'Hydraulic system', definition: 'A system that uses pressure in a liquid to multiply force.' },
  ],
  examQuestion: {
    prompt:
      'An elephant of weight 40,000 N has 4 feet, each with an area of 0.05 m². A man of weight 800 N stands on stiletto heels of total area 0.0001 m². (a) Calculate the pressure each puts on the ground. (b) Explain who damages the floor more, with reasoning. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Elephant total foot area = 4 × 0.05 = 0.2 m²',
      'P_elephant = 40,000 ÷ 0.2 = 200,000 Pa',
      'P_stiletto = 800 ÷ 0.0001 = 8,000,000 Pa',
      'Stilettos exert FAR MORE pressure',
      'because the area is tiny compared with the force',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-9.10.w1', type: 'text', question: 'Pressure = force ÷ ___ .', answer: 'area', hint1: '4 letters', hint2: 'Surface', hint3: 'Starts with a' },
      { id: 'phys-9.10.w2', type: 'number', question: '50 N over 2 m². Pressure (Pa)?', answer: '25', hint1: '50 ÷ 2', hint2: 'Two digits', hint3: 'Twenty-five' },
      { id: 'phys-9.10.w3', type: 'choice', question: 'Why are knives sharp?', options: ['Looks nice', 'Small area → high pressure → easier to cut', 'Cheaper', 'Lighter'], answer: 'Small area → high pressure → easier to cut', hint1: 'Tiny contact area', hint2: 'Concentrate force', hint3: 'High P' },
    ],
    level1: [
      { id: 'phys-9.10.1', type: 'choice', question: 'Pressure unit is the…', options: ['Newton', 'Pascal', 'Joule', 'Watt'], answer: 'Pascal', hint1: '6 letters', hint2: 'Symbol Pa', hint3: 'After French scientist' },
      { id: 'phys-9.10.2', type: 'number', question: '600 N over 1.5 m². Pressure (Pa)?', answer: '400', hint1: '600 ÷ 1.5', hint2: 'Three digits', hint3: 'Four hundred' },
    ],
    level2: [
      { id: 'phys-9.10.3', type: 'choice', question: 'Why does liquid pressure increase with depth?', options: ['Water gets denser', 'More weight of water above pushes down', 'It gets cooler', 'Light fades'], answer: 'More weight of water above pushes down', hint1: 'Cumulative weight', hint2: 'Each metre adds load', hint3: 'Total downward force' },
      { id: 'phys-9.10.4', type: 'choice', question: 'Snowshoes spread weight over large area to…', options: ['Increase pressure', 'Decrease pressure (so you don\u2019t sink)', 'Look bigger', 'Catch more wind'], answer: 'Decrease pressure (so you don\u2019t sink)', hint1: 'Big area = low P', hint2: 'Walk on snow', hint3: 'Soft ground trick' },
    ],
    level3: [
      { id: 'phys-9.10.5', type: 'number', question: 'A box has weight 200 N, base area 0.25 m². Pressure (Pa)?', answer: '800', hint1: '200 ÷ 0.25', hint2: 'Three digits', hint3: 'Eight hundred' },
      { id: 'phys-9.10.6', type: 'text', question: 'Atmospheric pressure at sea level is about 100,000 ___ .', answer: 'Pa', hint1: '2 characters', hint2: 'Pascal symbol', hint3: 'Pressure unit' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  SPEED_TOPIC,
  DT_TOPIC,
  RELATIVE_TOPIC,
  FORCES_TOPIC,
  FRICTION_TOPIC,
  RESISTANCE_TOPIC,
  DIAGRAMS_TOPIC,
  MOMENTS_TOPIC,
  ELASTICITY_TOPIC,
  PRESSURE_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const forcesAndMotionTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
